import pluralize from "pluralize"

import isEmptyObject from "~/helpers/is-empty-object"
import { getRelationship } from "./getRelationship"

/**
 * Collect resources from the Vuex store using the relationships map
 *
 * @export
 * @template T
 * @param {T['relationships']} [relationships={}]
 * @param {*} rootGetters
 * @returns
 */
export function getRelationships<T extends JsonApi.ResourceObject>(
  relationships: T['relationships'],
  rootGetters: any = {},
  includeRelationships: string | string[] = [],
  getterOverrides: { [type: string]: string } = {}
): Partial<T['attributes']> {
  if (typeof relationships === 'object' && !isEmptyObject(relationships)) {

    return Object.entries<JsonApi.Relationship<T>>(relationships).reduce((
      collected: Partial<T['attributes']>,
      [key, relationship]: [string, JsonApi.Relationship<T>]
    ) => {
      const { data } = relationship
      const includedKeys: string[] = Array.isArray(includeRelationships) ? includeRelationships : !!includeRelationships ? [includeRelationships] : []

      // don't get relationships if not asked
      if (!data || includedKeys.length <= 0) {
        return {
          ...collected,
          [key]: data
        }
      }

      // Include the "list" of relationships
      if (Array.isArray(data)) {
        const collectedRelationships = data.map((relationship: JsonApi.RelationshipData) => {
          if (includedKeys.includes(pluralize.singular(relationship.type))) {
            return { ...relationship, ...getRelationship(relationship, rootGetters, getterOverrides) }
          }

          return relationship
        })

        return {
          ...collected,
          [key]: collectedRelationships
        }
      }

      const isValidDataObj: boolean = data && !!data.id && !!data.type
      const hasIncludedKey = isValidDataObj && includedKeys.includes(pluralize.singular(data.type))

      // Include the "single" relationship
      return {
        ...collected,
        [key]: hasIncludedKey && isValidDataObj ? getRelationship(data, rootGetters, getterOverrides) : data
      }
    }, {})
  }

  return {}
}
